import React, { useEffect, useMemo } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import App, { AppProps, AppContext } from 'next/app';
import { appWithTranslation } from 'next-i18next';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getCookie, deleteCookie } from 'cookies-next';

import AuthLayoutComponent from '@components/AuthLayout';
import { API, APIRoutes } from '@utils/api';
import { ThemeProvider } from '@emotion/react';
import theme from '@styles/theme';
import GlobalStyles from '@styles/globalStyles';
import Layout from '@components/Layout';
import AcceptLayout from '@components/AcceptLayout';
import { AppContextProvider } from '@context/AppContext';
import { setItem, KEYS } from '@utils/cache';
import * as gtag from '@utils/gtag';

import routes from '@constants/routes.json';

type MyAppProps = AppProps & {
  mainNavigation: MainNavigation;
  footerNavigation: FooterNavigation;
  referer: string | null;
  userProfile: User;
  isAuthenticated: boolean;
};

const AUTH_ROUTES = [
  routes.signin.destination,
  routes.signup.destination,
  routes.passwordReset.destination,
  routes.passwordChange.destination.split('/').slice(0, -1).join('/'),
  routes.accountConfirmation.destination,
];

const queryClient = new QueryClient();

// The authenticatedRoute/withAppContext HOC composition doesn't fully strip its
// injected props (context, renderComponent) from the exported component's type,
// so it doesn't structurally satisfy React.ComponentType when rendered directly.
const AuthLayout = AuthLayoutComponent as React.ComponentType<{
  children: React.ReactNode;
  referer: string | null;
}>;

function MyApp({
  Component,
  pageProps,
  mainNavigation,
  footerNavigation,
  referer,
  userProfile,
  isAuthenticated,
}: MyAppProps) {
  const { route, locale, defaultLocale, events } = useRouter();
  const isAuthPage = useMemo(
    () => AUTH_ROUTES.includes(route) || AUTH_ROUTES.some((authRoute) => route.startsWith(authRoute)),
    [route],
  );
  const isAcceptPage = useMemo(() => route === routes.acceptTerms.destination, [route]);

  useEffect(() => {
    if (isAuthenticated && userProfile) {
      setItem(KEYS.USER_PROFILE, JSON.stringify(userProfile));
    }
  }, [isAuthenticated, userProfile]);

  useEffect(() => {
    API.defaults.headers['Accept-Language'] = locale || defaultLocale || '';
  }, [locale, defaultLocale]);

  useEffect(() => {
    const handleRouteChange = (url: URL) => {
      gtag.pageview(url);
    };
    events.on('routeChangeComplete', handleRouteChange);
    return () => {
      events.off('routeChangeComplete', handleRouteChange);
    };
  }, [events]);

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient} contextSharing>
          <AppContextProvider userProfile={userProfile} isAuthenticated={isAuthenticated}>
            <GlobalStyles />

            {isAuthPage && (
              <AuthLayout referer={referer}>
                <Component {...pageProps} />
              </AuthLayout>
            )}
            {isAcceptPage && (
              <AcceptLayout>
                <Component {...pageProps} />
              </AcceptLayout>
            )}
            {!isAcceptPage && !isAuthPage && (
              <Layout mainNavigation={mainNavigation} footerNavigation={footerNavigation}>
                <Component {...pageProps} />
              </Layout>
            )}
            <ToastContainer
              autoClose={8000}
              position="bottom-center"
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              pauseOnFocusLoss
              draggable
              pauseOnHover
              closeButton={false}
            />
          </AppContextProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ThemeProvider>
    </>
  );
}

const ERROR_PAGES = ['/404', '/500'];

MyApp.getInitialProps = async (appContext: AppContext) => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const currentLang = appContext.ctx.locale;
  const defaultLang = appContext.ctx.defaultLocale;
  const currentPath = appContext.router.route;
  const appProps = await App.getInitialProps(appContext);
  const { req, res } = appContext.ctx;
  API.defaults.headers['Accept-Language'] = currentLang || defaultLang || '';

  const cookieOptions = { ...(req ? { req } : {}), ...(res ? { res } : {}) };

  let userProfile: User | null = null;
  let isAuthenticated = false;
  if (!ERROR_PAGES.includes(appContext.router.route)) {
    // CHECK IF USER IS AUTHENTICATED BASED ON AUTH COOKIE
    const token = getCookie(KEYS.AUTH_TOKEN, cookieOptions);

    delete API.defaults.headers.common['authorization'];
    if (token) {
      API.defaults.headers.common['authorization'] = token;
    }

    try {
      const { data } = await API.get<{ user: User }>(APIRoutes.me);
      isAuthenticated = true;
      userProfile = data.user;
    } catch {
      deleteCookie(KEYS.AUTH_TOKEN, cookieOptions);
      isAuthenticated = false;
    }
    if (
      isAuthenticated &&
      userProfile &&
      !(currentPath === routes.acceptTerms.destination) &&
      !(userProfile.rodo && userProfile.termsAndConditions)
    ) {
      const redir = `/${currentLang || defaultLang}${routes.acceptTerms.source}`;
      // console.log(`redirecting from: ${currentPath} to:  ${redir}`)
      if (res) {
        // console.log("302")
        res.writeHead(302, { Location: redir });
        res.end();
      } else {
        // console.log('ROUTER')
        void appContext.router.replace(redir);
      }
      // console.log('ENDING');
      return {};
    }
  }

  type NavData = { navigation: MainNavigation & { footerMenu: FooterNavigation } };
  const { data: navData } = await API.get<NavData>(APIRoutes.navigation);

  const mainNavigation: MainNavigation = { ...navData.navigation };
  const footerNavigation: FooterNavigation = navData.navigation.footerMenu;
  delete (mainNavigation as MainNavigation & { footerMenu?: FooterNavigation }).footerMenu;

  return {
    ...appProps,
    mainNavigation,
    footerNavigation,
    referer: appContext.ctx.req?.headers['referer'] ?? null,
    userProfile,
    isAuthenticated,
  };
};

export default appWithTranslation(MyApp);
